/**
 * Promo Modal - Custom Web Component
 * Simple, versatile promotional modal
 */

class PromoModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
  }

  connectedCallback() {
    // Query elements after they're in the DOM
    this.overlay = this.querySelector('.promo-modal__overlay');
    this.container = this.querySelector('.promo-modal__container');
    this.closeBtn = this.querySelector('.promo-modal__close');
    this.closeOnClickBtns = this.querySelectorAll('[data-close-modal]');
    this.storageKey = `promo-modal-${this.id}-closed`;

    this.bindEvents();

    // Check if should auto-show
    if (this.shouldAutoShow()) {
      this.scheduleAutoShow();
    }

    // Setup trigger buttons
    this.setupTriggers();
  }

  bindEvents() {
    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Buttons that close modal
    this.closeOnClickBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        // Allow link to work, then close
        setTimeout(() => this.close(), 100);
      });
    });

    // Prevent clicks inside container from closing
    if (this.container) {
      this.container.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }
  }

  setupTriggers() {
    // Find any trigger buttons for this modal
    const triggers = document.querySelectorAll(`[data-modal-trigger="${this.id}"]`);
    triggers.forEach(trigger => {
      trigger.addEventListener('click', () => this.open());
    });
  }

  shouldAutoShow() {
    const autoShow = this.dataset.autoShow === 'true';
    if (!autoShow) return false;

    const shouldRemember = this.dataset.remember === 'true';
    if (!shouldRemember) return true;

    // Check if we should skip due to previous close
    const closedData = localStorage.getItem(this.storageKey);
    if (!closedData) return true;

    try {
      const { timestamp } = JSON.parse(closedData);
      const rememberHours = parseInt(this.dataset.rememberHours, 10) || 24;
      const expiryTime = timestamp + (rememberHours * 60 * 60 * 1000);

      if (Date.now() > expiryTime) {
        // Expired, clear storage and show
        localStorage.removeItem(this.storageKey);
        return true;
      }

      return false; // Still within remember period
    } catch (e) {
      localStorage.removeItem(this.storageKey);
      return true;
    }
  }

  scheduleAutoShow() {
    const delay = parseInt(this.dataset.delay, 10) || 0;
    const delayMs = delay * 1000;

    if (delayMs === 0) {
      // Small delay to ensure page is rendered
      requestAnimationFrame(() => this.open());
    } else {
      setTimeout(() => this.open(), delayMs);
    }
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('promo-modal-open');

    // Focus management
    this.previousActiveElement = document.activeElement;

    // Focus the close button or first focusable element
    requestAnimationFrame(() => {
      const focusTarget = this.closeBtn || this.querySelector('button, [href], input, select, textarea');
      if (focusTarget) focusTarget.focus();
    });

    // Trap focus within modal
    this.trapFocus();
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('promo-modal-open');

    // Remember close if configured
    if (this.dataset.remember === 'true') {
      localStorage.setItem(this.storageKey, JSON.stringify({
        timestamp: Date.now()
      }));
    }

    // Restore focus
    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
    }

    // Remove focus trap
    this.removeFocusTrap();
  }

  trapFocus() {
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    this.firstFocusable = focusableElements[0];
    this.lastFocusable = focusableElements[focusableElements.length - 1];

    this.handleFocusTrap = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === this.firstFocusable) {
          e.preventDefault();
          this.lastFocusable.focus();
        }
      } else {
        if (document.activeElement === this.lastFocusable) {
          e.preventDefault();
          this.firstFocusable.focus();
        }
      }
    };

    this.addEventListener('keydown', this.handleFocusTrap);
  }

  removeFocusTrap() {
    if (this.handleFocusTrap) {
      this.removeEventListener('keydown', this.handleFocusTrap);
    }
  }
}

// Register the custom element
if (!customElements.get('promo-modal')) {
  customElements.define('promo-modal', PromoModal);
}
